import { z } from 'zod';
import { uuid, isoTimestamp, syncColumns } from '../schemas/common.js';
import { TODO_SYNC_TABLES } from './enums.js';
import { todoRecurrence } from './recurrence.js';
import { todoLogEntry } from './log.js';
/**
 * Row and protocol shapes for the Todo module.
 *
 * Like the agenda schemas, `household_id` is deliberately absent from the row —
 * it is derived from the session server-side, never a parameter a client sends —
 * and the module declares its own mutation union and pull response over its one
 * table rather than sharing groceries'.
 */
export const todoSyncTable = z.enum(TODO_SYNC_TABLES);
/* ---------------- row ---------------- */
export const todoRow = syncColumns.extend({
    id: uuid,
    title: z.string().min(1).max(200),
    done: z.boolean().default(false),
    /** The household member responsible, or null for "anyone". */
    assigned_to: uuid.nullish(),
    /**
     * When to remind, and — for a recurring todo — the instant the recurrence
     * steps from; null = no reminder. A recurring todo always carries one.
     */
    scheduled_at: isoTimestamp.nullish(),
    /**
     * A periodic rule (daily / every N days / specific weekdays), or null for a
     * one-off. Only the rule is stored; ticking a recurring todo rolls
     * `scheduled_at` forward rather than marking it done (see recurrence.ts).
     */
    recurrence: todoRecurrence.nullish(),
    /** Free-text grouping label ("Huis", "Boodschappen"), or null. */
    category: z.string().max(60).nullish(),
    /**
     * A little activity log — what happened to this todo, by whom, and when
     * (created / completed / reopened / recurred / edited). Rides the row through
     * the ordinary LWW sync; null = no history yet. See todos/log.ts.
     */
    log: z.array(todoLogEntry).nullish(),
    /**
     * Fractional sort key. A reorder writes only the moved row's new position
     * (the average of its new neighbours'), so two phones dragging different todos
     * never conflict.
     */
    position: z.number(),
    created_by: uuid.nullish(),
});
/* ---------------- mutations ---------------- */
const todoMutationId = z.object({ id: uuid });
const todoUpsert = todoMutationId.extend({
    op: z.literal('upsert'),
    table: z.literal('todos'),
    row: todoRow,
});
/** A soft delete. Rows are never hard-deleted; the tombstone propagates. */
const todoDelete = todoMutationId.extend({
    op: z.literal('delete'),
    table: todoSyncTable,
    row: z.object({ id: uuid, updated_at: isoTimestamp }),
});
export const todoMutation = z.union([todoUpsert, todoDelete]);
export const todoPushRequest = z.object({
    mutations: z.array(todoMutation).max(500),
});
/** Same shape as the platform push response — applied ids + validation rejections. */
export const todoPushResponse = z.object({
    applied: z.array(uuid),
    rejected: z.array(z.object({ id: uuid, code: z.string(), message: z.string() })),
    server_time: isoTimestamp,
});
export const todoPullResponse = z.object({
    server_time: isoTimestamp,
    changes: z.object({
        todos: z.array(z.record(z.unknown())),
    }),
    has_more: z.boolean(),
    cursor: z.string().optional(),
    full_resync: z.boolean().optional(),
});
/* ---------------- read: the ordered list ---------------- */
/**
 * `GET /api/v1/todos`. The household's todos in display order, which the client
 * also holds in its local mirror — this endpoint backs first paint, the contract
 * test and anything that wants the truth without a full sync. The element schema
 * is the row schema itself: the read shape is the stored shape minus
 * `household_id` and `synced_at`, exactly what the sync pull emits.
 */
export const todoListResponse = z.object({
    todos: z.array(todoRow),
});
//# sourceMappingURL=schemas.js.map